/**
 * Coach video-analysis preferences (browser-local).
 * Applied when enqueueing pose / ball jobs from the review player / session batch.
 */

export const VIDEO_ANALYSIS_SETTINGS_KEY = "smarttt.videoAnalysisSettings";

export const VIDEO_ANALYSIS_POSE_FPS_OPTIONS = [7.5, 10, 15, 20, 30, 60];

/** Ball detection sample rates (pipeline --ball_detection_fps). */
export const VIDEO_ANALYSIS_BALL_FPS_OPTIONS = [15, 30, 60, 120];

/** Which court-side player(s) to run pose detection on. A = left (G), B = right (D). */
export const VIDEO_ANALYSIS_POSE_PLAYER_OPTIONS = Object.freeze(["A", "B", "Both"]);

export const DEFAULT_VIDEO_ANALYSIS_SETTINGS = Object.freeze({
  version: 1,
  /** Target pose samples per second of video time (pipeline --pose_fps). */
  poseFps: 30,
  /** Target ball detections per second of video time (pipeline --ball_detection_fps). */
  ballDetectionFps: 60,
  /** Pose detection target: Player A, Player B, or both. */
  poseDetectionPlayer: "Both",
});

function clampPoseFps(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_VIDEO_ANALYSIS_SETTINGS.poseFps;
  return Math.max(0.5, Math.min(60, n));
}

function clampBallDetectionFps(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    return DEFAULT_VIDEO_ANALYSIS_SETTINGS.ballDetectionFps;
  }
  return Math.max(1, Math.min(120, n));
}

export function normalizePoseDetectionPlayer(raw) {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  if (s === "b" || s === "right" || s === "d") return "B";
  if (s === "both" || s === "ab" || s === "a+b" || s === "all") return "Both";
  if (s === "a" || s === "left" || s === "g" || s === "") return "A";
  // Preserve exact allowed labels if already canonical.
  if (VIDEO_ANALYSIS_POSE_PLAYER_OPTIONS.includes(raw)) return raw;
  return DEFAULT_VIDEO_ANALYSIS_SETTINGS.poseDetectionPlayer;
}

/**
 * Pick roi_left / roi_right for the API from calibrated boxes + player setting.
 * A → left only; B → right only; Both → both.
 */
export function roisForPoseDetectionPlayer(player, rois) {
  const mode = normalizePoseDetectionPlayer(player);
  const left = rois?.left || rois?.roiLeft || rois?.roi_left || null;
  const right = rois?.right || rois?.roiRight || rois?.roi_right || null;
  if (mode === "B") return { roiLeft: null, roiRight: right };
  if (mode === "Both") return { roiLeft: left, roiRight: right };
  return { roiLeft: left, roiRight: null };
}

export function normalizeVideoAnalysisSettings(input) {
  const src = input && typeof input === "object" ? input : {};
  return {
    version: 1,
    poseFps: clampPoseFps(src.poseFps ?? src.pose_fps),
    ballDetectionFps: clampBallDetectionFps(
      src.ballDetectionFps ?? src.ball_detection_fps
    ),
    poseDetectionPlayer: normalizePoseDetectionPlayer(
      src.poseDetectionPlayer ?? src.pose_detection_player ?? src.players
    ),
  };
}

export function loadVideoAnalysisSettings() {
  if (typeof window === "undefined") {
    return { ...DEFAULT_VIDEO_ANALYSIS_SETTINGS };
  }
  try {
    const raw = window.localStorage.getItem(VIDEO_ANALYSIS_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_VIDEO_ANALYSIS_SETTINGS };
    return normalizeVideoAnalysisSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_VIDEO_ANALYSIS_SETTINGS };
  }
}

export function saveVideoAnalysisSettings(next) {
  const normalized = normalizeVideoAnalysisSettings(next);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        VIDEO_ANALYSIS_SETTINGS_KEY,
        JSON.stringify(normalized)
      );
    } catch {
      /* ignore quota / private mode */
    }
  }
  return normalized;
}

/** Notify open players/dialogs that settings changed. */
export function notifyVideoAnalysisSettingsChanged(settings) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("smarttt:video-analysis-settings", {
      detail: normalizeVideoAnalysisSettings(settings),
    })
  );
}
